import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { DataConverter } from "../../../../Global/Base/Converters/DataConverter";
import { Flags } from "../../../../OW.Remoting/Remoting/DataTypes/Flags";
import { registerCustomElement } from "../../../UI.ElementarControls/Utils/CustomElementUtils";
import { CalendarItemTemplate } from "../Templates/DataTypeTemplates/CalendarItemTemplate";
import { Column } from "./Column";
import { ICalendarColumnDefinition } from "./ColumnFactory";

const logger = LazyLoadingSectionLogger.create(LoggingSection.calendarFrame);

export class CalendarItem extends Column {
    //#region Fields
    private _objectName: string;

    /**
     * Název atributu, který obsahuje popisek pro kalendářové položky.
     */
    private _captionFld: string;

    /**
     * Název atributu, který určuje počáteční datum popřípadě i čas události.
     */
    private _dateFromFld: string;

    /**
     * Název atributu, který určuje koncové datum popřípadě i čas události.
     */
    private _dateToFld: string;
    private _condition: string;
    private _color: string = "fBlue";
    private _includeTime: boolean = false;
    private _showFormID: string;
    private _classOrder: number = 0;
    private _canInsert: boolean = true;
    private _textFields: string[];
    //#endregion

    public get objectName(): string {
        return this._objectName;
    }

    public set objectName(v: string) {
        this._objectName = v;
    }

    public get condition(): string {
        return this._condition;
    }

    public get captionFld(): string {
        return this._captionFld;
    }

    public get dateFromFld(): string {
        return this._dateFromFld;
    }

    public get dateToFld(): string {
        return this._dateToFld;
    }

    public get color(): string {
        return this._color;
    }

    public get includeTime(): boolean {
        return this._includeTime;
    }

    public get showFormID(): string {
        return this._showFormID;
    }

    public get canInsert(): boolean {
        return this._canInsert;
    }

    public get classOrder(): number {
        return this._classOrder;
    }

    public set classOrder(v: number) {
        this._classOrder = v;
    }

    public get itemId(): string {
        return `${this._objectName}#${this._classOrder}`; // `
    }

    public get textFields(): string[] {
        return this._textFields;
    }

    //#region Methods

    //#endregion Methods

    protected static override get observedAttributes(): string[] {
        return ["data-objectname", "data-captionfld", "data-datefromfld", "data-datetofld", "data-condition", "data-itemcolor", "data-includetime", "data-showformid", "data-textfields", "data-caninsert"].concat(Column.observedAttributes);
    }

    /**
     * Zinicializuje data sloupce
     * @param definition - definice sloupce
     * @param params - dodatečné parametry
     */
    public override initialize(definition: ICalendarColumnDefinition): void {
        super.initialize(definition);

        // Tyto vlastnosti naplníme ihned, sahá se na ně během vytváření sloupců při vytváření dataSource (ještě nemusel proběhnout attributeChangedCallback)
        this._objectName = definition.objectName;
        this._textFields = definition.textFields;
        this._captionFld = definition.captionfld;
        this._dateFromFld = definition.dateFromfld;
        this._dateToFld = definition.dateTofld;
    }

    /**
     * Reakce na změny atributu
     * @param attrName - název změněného atributu
     * @param oldVal - původní hodnota
     * @param newVal - nová hodnota
     */
    protected override attributeChangedCallback(attrName: string, oldVal: string, newVal: string): void {
        super.attributeChangedCallback(attrName, oldVal, newVal);

        switch (attrName) {
            case "data-objectname":
                this._objectName = newVal;
                break;
            case "data-showformid":
                this._showFormID = newVal;
                break;
            case "data-captionfld":
                this._captionFld = newVal;
                break;
            case "data-datefromfld":
                this._dateFromFld = newVal;
                break;
            case "data-datetofld":
                this._dateToFld = newVal;
                break;
            case "data-condition":
                this._condition = newVal;
                break;
            case "data-itemcolor":
                this._color = this._convertColor(newVal);
                break;
            case "data-includetime":
                this._includeTime = DataConverter.toBoolean(newVal, false);
                break;
            case "data-caninsert":
                this._canInsert = DataConverter.toBoolean(newVal, true);
                break;
            case "data-textfields":
                this._textFields = newVal.split(";");
                break;
        }
    }

    private _convertColor(value: string): string {
        /**
         * Snaží se řešit zpětnou kompatibilitu.
         * Dříve bylo obsahem výčtový typ Color (clBlack, clMaroon, clGreen, clOlive, clNavy, clPurple, clTeal, clGray, clSilver, clRed, clLime, clYellow, clBlue, clFuchsia, clAqua, clLtGray, clDkGray, clWhite, clMoneyGreen, clSkyBlue, clCream, clMedGray)
         * a my teď chceme používat pouze typ Flag (fBlue, fRed), který má méně a některé jiné položky
         * Takže se to snažím převést co nejlépe na flagy, protože jen pro ně existují styly
         */

        let processedVal: string = value;
        if (value == null || value === "")
            processedVal = "fGreen";
        else if (value.startsWith("cl"))
            processedVal = `f${value.substr(2)}`;
        else if (!value.startsWith("f"))
            processedVal = `f${value}`;

        if (!Flags.get(processedVal)) {
            logger.warn(`Color '${value}' is not a Flag. Using default fGreen instead.`); //`
            processedVal = "fGreen";
        }

        return processedVal;
    }

    //#region HTMLElement
    protected override _constructor(): void {
        super._constructor();

        this.itemTemplate = new CalendarItemTemplate();
        //this.editItemTemplate = new Templates.BooleanEditTemplate();
    }

    //#endregion HTMLElement
} //end Column

registerCustomElement("tsc-xgCalendarItem", CalendarItem);
